import { useCallback, useEffect, useRef } from 'react';
import { Link, useParams } from 'react-router';
import { useForm, useWatch } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { useQuery } from '@tanstack/react-query';

import { ROUTES } from '#/config/routes';
import {
  createMtrTemplate,
  fetchMtrTemplateDetail,
  updateMtrTemplate,
} from '#/api/imsMgmt/settings/mtrTemplate';
import FloatingInput from '#/modules/commonComponents/form/FloatingInput';
import FloatingTextarea from '#/modules/commonComponents/form/FloatingTextarea';
import FloatingSelect from '#/modules/commonComponents/form/select';
import FormValidationSummary from '#/modules/commonComponents/form/FormValidationSummary';
import { Button } from '#/modules/commonComponents/button';
import PageShell from '#/modules/infra/layout/PageShell';
import useCreateEditSave from '#/hooks/useCreateEditSave';
import useFormValidationSummary from '#/hooks/useFormValidationSummary';
import MtrTemplateSpecsEditor from '#/modules/imsMgmt/settings/mtrTemplate/components/MtrTemplateSpecsEditor';
import MtrSerialFormatterField from '#/modules/imsMgmt/settings/mtrTemplate/components/MtrSerialFormatterField';
import useMtrTemplateForm, {
  buildMtrTemplateMutationPayload,
  mapMtrTemplateDetailToForm,
} from '#/modules/imsMgmt/settings/mtrTemplate/hooks/useMtrTemplateForm';
import {
  MTR_TEMPLATE_DEFAULT_VALUES,
  mtrTemplateFormSchema,
} from '#/modules/imsMgmt/settings/mtrTemplate/schemas/mtrTemplateSchema';
import {
  getMtrTemplateApiErrorMessage,
  isMtrTemplateVersionConflict,
} from '#/modules/imsMgmt/settings/mtrTemplate/helper/apiError';
import {
  MTR_TEMPLATE_FIELD_LABELS,
  resolveMtrTemplateFieldLabel,
} from '#/modules/imsMgmt/settings/mtrTemplate/helper/formValidationLabels';
import {
  mtrTemplateDetailQueryKey,
  mtrTemplateListQueryKeyPrefix,
} from '#/utils/queryKeys';

const CreateEditMtrTemplatePage = () => {
  const { id } = useParams();
  const updatedAtRef = useRef(null);
  const seedDefaultsRef = useRef(() => {});
  const {
    isSubmitAttempted,
    markSubmitAttempted,
    resetValidationSummary,
    customMessages,
  } = useFormValidationSummary();

  const detailQuery = useQuery({
    queryKey: mtrTemplateDetailQueryKey(id),
    queryFn: () => fetchMtrTemplateDetail(id),
    enabled: Boolean(id),
  });

  const { isEdit, isPending, save } = useCreateEditSave({
    id,
    createFn: createMtrTemplate,
    updateFn: updateMtrTemplate,
    buildPayload: (values) => buildMtrTemplateMutationPayload(
      values,
      id ? updatedAtRef.current : null,
    ),
    listQueryKeyPrefix: mtrTemplateListQueryKeyPrefix,
    detailQueryKey: mtrTemplateDetailQueryKey,
    getEditPath: ROUTES.mtrTemplateEdit,
    getErrorMessage: (error) => getMtrTemplateApiErrorMessage(error, '儲存失敗'),
    isVersionConflict: isMtrTemplateVersionConflict,
    onVersionConflict: () => detailQuery.refetch(),
  });

  const {
    register,
    handleSubmit,
    reset,
    control,
    setValue,
    clearErrors,
    formState: { errors },
  } = useForm({
    resolver: zodResolver(mtrTemplateFormSchema),
    defaultValues: MTR_TEMPLATE_DEFAULT_VALUES,
  });

  const mtrStructMainId = useWatch({
    control,
    name: 'mtrStructMainId',
    defaultValue: '',
  });

  const specs = useWatch({
    control,
    name: 'specs',
    defaultValue: [],
  });

  const mtrStructSubId = useWatch({
    control,
    name: 'mtrStructSubId',
    defaultValue: '',
  });

  const serialFormatterRaw = useWatch({
    control,
    name: 'serialFormatter',
    defaultValue: '',
  });

  const {
    structMainOptions,
    structSubOptions,
    treeRows,
    specOptions,
    isOptionsLoading,
  } = useMtrTemplateForm(mtrStructMainId);

  const handleSeedReady = useCallback((seedFn) => {
    seedDefaultsRef.current = seedFn;
  }, []);

  useEffect(() => {
    const detail = detailQuery.data?.data;
    if (!detail || isOptionsLoading) {
      return;
    }

    updatedAtRef.current = detail.updatedAt ?? detail.record?.updatedAt ?? null;
    resetValidationSummary();
    reset(mapMtrTemplateDetailToForm(detail, treeRows));
  }, [detailQuery.data, isOptionsLoading, treeRows, reset, resetValidationSummary]);

  function handleMainChange() {
    setValue('mtrStructSubId', '', { shouldDirty: true, shouldValidate: false });
    clearErrors('mtrStructSubId');
  }

  function handleSubChange() {
    seedDefaultsRef.current();
  }

  function handleInvalidSubmit() {
    markSubmitAttempted();
  }

  function onSubmit(values) {
    markSubmitAttempted();
    save(values);
  }

  const pageTitle = isEdit ? '編輯物料模板' : '新增物料模板';
  const isPageLoading = isEdit && detailQuery.isLoading;
  const isDetailNotFound = isEdit && detailQuery.isError;
  const loadedDetail = detailQuery.data?.data;
  const formatterResetKey = loadedDetail
    ? `${loadedDetail.id}-${loadedDetail.updatedAt ?? loadedDetail.record?.updatedAt ?? ''}`
    : 'create';
  const formatterInitialRaw = isEdit
    ? (loadedDetail?.serialFormatter ?? '')
    : serialFormatterRaw;
  /** 保險 A：edit 等 reset 後 form specs 有 mtrSpecId 再 mount，避免首幀 [] 觸發 prune */
  const formSpecsHydrated = (specs ?? []).some((rule) => Boolean(rule?.mtrSpecId));
  const showSerialFormatter = !isEdit || formSpecsHydrated;

  return (
    <PageShell variant="formTall">
      <div className="layout-shell__body">
        <div className="mx-auto w-full max-w-6xl">
          <div className="create_edit_page__header">
            <h1 className="page_title mb-0!">
              <span className="border-l-5 border-primary-400 pl-2.5">{pageTitle}</span>
            </h1>
            <Button type="button" variant="outline" asChild>
              <Link to={ROUTES.mtrTemplate}>返回列表</Link>
            </Button>
          </div>

          {isPageLoading || isOptionsLoading ? (
            <p className="text-sm text-muted-foreground">載入中…</p>
          ) : isDetailNotFound ? (
            <p className="text-sm text-destructive">
              {getMtrTemplateApiErrorMessage(detailQuery.error, '找不到物料模板')}
            </p>
          ) : (
            <>
              <FormValidationSummary
                show={isSubmitAttempted}
                errors={errors}
                fieldLabels={MTR_TEMPLATE_FIELD_LABELS}
                fieldLabelResolver={resolveMtrTemplateFieldLabel}
                customMessages={customMessages}
              />
              <form
                className="sheet create_edit create_edit_form create_edit_form--page-scroll"
                onSubmit={handleSubmit(onSubmit, handleInvalidSubmit)}
                noValidate
              >
                <div className="create_edit_form__body divide-y divide-gray-500">
                  <section className="relative w-full pb-5">
                    <div className="form-section__header">
                      <h2 className="form-block__title mb-0! mr-2">基本資料</h2>
                    </div>
                    <div className="relative mt-5 grid w-full grid-cols-1 gap-4 lg:grid-cols-2">
                      <FloatingSelect
                        id="mtrStructMainId"
                        label="主分類"
                        options={structMainOptions}
                        control={control}
                        errors={errors}
                        onChange={handleMainChange}
                      />
                      <FloatingSelect
                        id="mtrStructSubId"
                        label="次分類"
                        options={structSubOptions}
                        control={control}
                        errors={errors}
                        isDisabled={!mtrStructMainId}
                        noOptionsMessage="請先選擇主分類"
                        onChange={handleSubChange}
                      />
                      <FloatingInput
                        id="code"
                        label="代號"
                        register={register}
                        errors={errors}
                      />
                      <FloatingInput
                        id="name"
                        label="名稱"
                        register={register}
                        errors={errors}
                      />
                      <div className="lg:col-span-2">
                        <FloatingTextarea
                          id="remark"
                          label="備註"
                          register={register}
                          errors={errors}
                        />
                      </div>
                    </div>
                  </section>

                  <section className="relative w-full pb-5">
                    <MtrTemplateSpecsEditor
                      control={control}
                      errors={errors}
                      specOptions={specOptions}
                    />
                  </section>

                  <section className="relative w-full pb-5">
                    {showSerialFormatter ? (
                      <MtrSerialFormatterField
                        key={formatterResetKey}
                        resetKey={formatterResetKey}
                        setValue={setValue}
                        errors={errors}
                        initialRaw={formatterInitialRaw}
                        mtrStructMainId={mtrStructMainId}
                        mtrStructSubId={mtrStructSubId}
                        specs={specs}
                        specOptions={specOptions}
                        structMainOptions={structMainOptions}
                        structSubOptions={structSubOptions}
                        onSeedReady={handleSeedReady}
                      />
                    ) : (
                      <p className="text-sm text-muted-foreground">載入序號格式…</p>
                    )}
                  </section>
                </div>

                <div className="create_edit_form__actions flex flex-wrap justify-end gap-2">
                  <Button type="button" variant="outline" asChild>
                    <Link to={ROUTES.mtrTemplate}>取消</Link>
                  </Button>
                  <Button type="submit" disabled={isPending}>
                    {isPending ? '儲存中…' : '儲存'}
                  </Button>
                </div>
              </form>
            </>
          )}
        </div>
      </div>
    </PageShell>
  );
};

export default CreateEditMtrTemplatePage;
